import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, TextField, Typography, alpha } from "@mui/material";
import { RemoveCircleOutline, Undo } from "@mui/icons-material";
import { AppDispatch } from "../../configureStore";
import { UPDATE_EXTRACTED_FIELD_VALUE } from "../../redux/reducer/returns";
import { ExtractedFormResult } from "../../interface/extracted";
import { FormExtraction, TaxReturnPage } from "../../interface/returns";

interface PageMetadataProps {
    currentPageData: ExtractedFormResult<any> | null;
    currentFormData: FormExtraction<any> | null;
    isCurrentPageRemoved: boolean;
    currentPageIndex: number;
    filteredPages: TaxReturnPage[];
    selectedFormType: string;
    onToggleRemovePage: () => void;
}

const PageMetadata = ({
    currentPageData,
    currentFormData,
    isCurrentPageRemoved,
    currentPageIndex,
    filteredPages,
    selectedFormType,
    onToggleRemovePage,
}: PageMetadataProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [taxYearValue, setTaxYearValue] = useState<string>("");
    const [taxYearError, setTaxYearError] = useState<string>("");
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const validateTaxYear = useCallback((year: string): string => {
        if (!year || year.trim() === "") {
            return "Tax year is required";
        }
        const yearNum = Number(year);
        if (Number.isNaN(yearNum)) {
            return "Tax year must be a valid number";
        }
        if (yearNum < 2000 || yearNum > 2099) {
            return "Tax year must be between 2000 and 2099";
        }
        return "";
    }, []);

    const handleTaxYearChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            setTaxYearValue(newValue);
            setTaxYearError("");

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                const error = validateTaxYear(newValue);
                if (error) {
                    setTaxYearError(error);
                    return;
                }

                if (selectedFormType && currentPageData && currentFormData?.extraction) {
                    const pageIndex = currentFormData.extraction.findIndex(
                        (e) => e.extractedData.originalPage === currentPageData.extractedData.originalPage,
                    );
                    if (pageIndex !== undefined && pageIndex !== -1) {
                        const path = `${selectedFormType}.extraction[${pageIndex}].extractedData.taxYear`;
                        dispatch({
                            type: UPDATE_EXTRACTED_FIELD_VALUE,
                            value: Number(newValue),
                            location: path,
                        });
                    }
                }
            }, 500);
        },
        [selectedFormType, currentPageData, currentFormData, validateTaxYear, dispatch],
    );

    // Sync taxYearValue with currentPageData when page changes
    useEffect(() => {
        if (currentPageData?.extractedData?.taxYear) {
            setTaxYearValue(String(currentPageData.extractedData.taxYear));
            setTaxYearError("");
        } else {
            setTaxYearValue("");
        }
    }, [currentPageData]);

    // Cleanup debounce timer on unmount
    useEffect(
        () => () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        },
        [],
    );

    return (
        <Box
            sx={{
                mb: 3,
                p: 1.5,
                backgroundColor: (t) => {
                    if (isCurrentPageRemoved) {
                        return t.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.08)";
                    }
                    return alpha(t.palette.action.disabled, 0.08);
                },
                borderRadius: 1,
                border: (t) =>
                    isCurrentPageRemoved ? `2px solid ${t.palette.error.main}` : `1px solid ${t.palette.divider}`,
            }}
        >
            {currentPageData ? (
                <>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 1000 }}
                    >
                        Page Information
                    </Typography>
                    {currentPageData.requiresManualReview && (
                        <Typography
                            variant="body2"
                            color="warning.main"
                        >
                            Manual Review Required
                        </Typography>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2">
                            Form Page: {currentPageData.extractedData.formPageNumber}
                        </Typography>
                        <Typography variant="body2">
                            Original Page: {currentPageData?.extractedData?.originalPage ?? "Unknown (ISSUE)"}
                        </Typography>
                        <TextField
                            label="Tax Year"
                            type="number"
                            size="small"
                            value={taxYearValue}
                            onChange={handleTaxYearChange}
                            error={!!taxYearError}
                            helperText={taxYearError}
                            sx={{ width: "120px" }}
                            slotProps={{
                                htmlInput: {
                                    min: 2000,
                                    max: 2099,
                                },
                            }}
                        />
                        <Typography variant="body2">
                            Confidence: {currentPageData.overallConfidence.toFixed(1)}%
                        </Typography>

                        {isCurrentPageRemoved ? (
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<Undo />}
                                onClick={onToggleRemovePage}
                            >
                                Restore Page
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<RemoveCircleOutline />}
                                onClick={onToggleRemovePage}
                            >
                                Remove Page
                            </Button>
                        )}
                    </Box>
                </>
            ) : (
                <>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "warning.main" }}
                    >
                        No Extraction Data
                    </Typography>
                    {filteredPages[currentPageIndex]?.form?.type && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Classified as: {filteredPages[currentPageIndex].form?.type}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        This page has no extraction data. It may be misclassified by the backend.
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<RemoveCircleOutline />}
                            onClick={onToggleRemovePage}
                        >
                            Remove Page
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PageMetadata;
